/**
 * @file element-renderer-factory.tsx
 * @description Factory for canvas element renderers using Registry Pattern
 *
 * Design Decisions:
 * - Factory Pattern enables extending renderers without modifying existing code (Open-Closed)
 * - Each element type has dedicated renderer class
 * - Renderers implement common interface but handle type-specific logic
 * - Singleton factory instance manages all renderer registrations
 * - Replaces large switch statements with polymorphic render calls
 *
 * Renderer Lifecycle:
 * 1. Factory instantiated as singleton
 * 2. Default renderers registered in constructor
 * 3. Custom renderers can be registered via factory.register()
 * 4. Template designer uses factory.getRenderer(type) for rendering
 *
 * Related:
 * - @see ./base-element-renderer.tsx - Base class all renderers extend
 * - @see ./placeholder-renderers.tsx - Placeholder element renderers
 * - @see ../../../utils/element-factory.ts - Element creation
 * - @see ../../../../ARCHITECTURE.md#adr-002-factory-pattern-for-renderers
 *
 * @example
 * const renderer = elementRendererFactory.getRenderer('invoice-number');
 * renderer.render(context, element);
 *
 * // Add custom renderer
 * elementRendererFactory.register('custom-type', new CustomRenderer());
 */

import type { ElementTypeRenderer } from "./base-element-renderer";
import { TextRenderer } from "./text-renderer";
import { HeaderRenderer } from "./header-renderer";
import { MessageRenderer } from "./message-renderer";
import { ImageRenderer } from "./image-renderer";
import { TableRenderer } from "./table-renderer";
import { QRCodeRenderer } from "./qrcode-renderer";
import {
  InvoiceNumberRenderer,
  InvoiceDateRenderer,
  PaymentDueDateRenderer,
  OrderNumberRenderer,
  LeitwegIdRenderer,
  PageNumberRenderer,
  SmallBusinessNoteRenderer,
  SenderRenderer,
  RecipientRenderer,
  CustomerNumberRenderer,
  IbanRenderer,
  SwiftRenderer,
  BankNameRenderer,
  EmailRenderer,
  WebsiteRenderer,
  CourtRenderer,
  HrnRenderer,
  CountryRenderer,
  TaxNumberRenderer,
  VatIdRenderer,
  InvoiceInfoBlockRenderer,
} from "./placeholder-renderers";

/**
 * Factory for creating element renderers
 * Follows Single Responsibility and Open-Closed principles
 */
class ElementRendererFactory {
  private renderers: Map<string, ElementTypeRenderer> = new Map();
  private static instance: ElementRendererFactory;

  private constructor() {
    this.registerDefaultRenderers();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ElementRendererFactory {
    if (!ElementRendererFactory.instance) {
      ElementRendererFactory.instance = new ElementRendererFactory();
    }
    return ElementRendererFactory.instance;
  }

  /**
   * Register all default element renderers
   */
  private registerDefaultRenderers() {
    // Content elements
    this.register("text", new TextRenderer());
    this.register("header", new HeaderRenderer());
    this.register("message", new MessageRenderer());
    this.register("image", new ImageRenderer());
    this.register("table", new TableRenderer());
    this.register("qrcode", new QRCodeRenderer());

    // Invoice data placeholders
    this.register("invoice-number", new InvoiceNumberRenderer());
    this.register("invoice-date", new InvoiceDateRenderer());
    this.register("payment-due-date", new PaymentDueDateRenderer());
    this.register("order-number", new OrderNumberRenderer());
    this.register("leitweg-id", new LeitwegIdRenderer());
    this.register("page-number", new PageNumberRenderer());
    this.register("small-business-note", new SmallBusinessNoteRenderer());

    // Address placeholders
    this.register("sender", new SenderRenderer());
    this.register("recipient", new RecipientRenderer());
    this.register("customer-number", new CustomerNumberRenderer());

    // Bank info placeholders
    this.register("iban", new IbanRenderer());
    this.register("swift", new SwiftRenderer());
    this.register("bank-name", new BankNameRenderer());

    // Contact info placeholders
    this.register("email", new EmailRenderer());
    this.register("website", new WebsiteRenderer());

    // Company info placeholders
    this.register("court", new CourtRenderer());
    this.register("hrn", new HrnRenderer());
    this.register("country", new CountryRenderer());
    this.register("tax-number", new TaxNumberRenderer());
    this.register("vat-id", new VatIdRenderer());

    // Composite elements
    this.register("invoice-info-block", new InvoiceInfoBlockRenderer());
  }

  /**
   * Register a renderer for a specific element type
   * Allows extending the factory with custom renderers
   */
  register(elementType: string, renderer: ElementTypeRenderer): void {
    this.renderers.set(elementType, renderer);
  }

  /**
   * Get renderer for element type
   */
  getRenderer(elementType: string): ElementTypeRenderer | undefined {
    return this.renderers.get(elementType);
  }

  /**
   * Check if renderer exists for element type
   */
  hasRenderer(elementType: string): boolean {
    return this.renderers.has(elementType);
  }
}

// Export singleton instance
export const elementRendererFactory = ElementRendererFactory.getInstance();
